import { ConnectProto } from "@racoon/api";

type RouteGroupOption = { id: string; name: string };

/** The zone's divisions in order; the first is the default for a new job. */
export const zoneNames = (cg: Pick<ConnectProto.ContainerGroup, "serviceProviders">): string[] =>
  (cg.serviceProviders ?? []).map((org) => org.name);

/** The group's route groups by job type, types in first-seen order. */
export const routeGroupsByJobType = (
  cg: Pick<ConnectProto.ContainerGroup, "routeGroups">,
): { jobType: ConnectProto.JobType; names: string[] }[] => {
  const byType = new Map<ConnectProto.JobType, string[]>();
  for (const rg of cg.routeGroups ?? []) {
    byType.set(rg.jobType, [...(byType.get(rg.jobType) ?? []), rg.name]);
  }
  return [...byType].map(([jobType, names]) => ({ jobType, names }));
};

/** The route groups a job of this type can take on the group; every bound one before a type is picked. */
export const routeGroupOptions = (
  cg: Pick<ConnectProto.ContainerGroup, "routeGroups">,
  jobType?: ConnectProto.JobType,
): RouteGroupOption[] => {
  const options = new Map<string, RouteGroupOption>();
  for (const rg of cg.routeGroups ?? []) {
    if (jobType !== undefined && rg.jobType !== jobType) continue;
    const id = String(rg.pickupLocationGroupId);
    if (!options.has(id)) options.set(id, { id, name: rg.name });
  }
  return [...options.values()];
};

/** Whether the zone differs from the stored one: an update sends it only then, unset keeps the stored zone. */
export const zoneChanged = (next: string[], initial: string[]): boolean =>
  next.length !== initial.length || next.some((id, i) => id !== initial[i]);
